import React, { useEffect, useState } from 'react';
import { UploadIcon, CheckIcon, FileTextIcon, AlertCircleIcon } from 'lucide-react';
interface KYCVerificationProps {
  onNext: () => void;
}
const KYCVerification: React.FC<KYCVerificationProps> = ({
  onNext
}) => {
  // Automatically set bank statement as required document
  const [bankStatementUploaded, setBankStatementUploaded] = useState(false);
  // Keep ID type selection for additional verification if needed
  const [idType, setIdType] = useState('');
  const [idFrontUploaded, setIdFrontUploaded] = useState(false);
  const [idBackUploaded, setIdBackUploaded] = useState(false);
  const [addressDocUploaded, setAddressDocUploaded] = useState(false);
  const handleUpload = (documentType: string) => {
    // In a real app, this would handle file uploads
    if (documentType === 'id-front') {
      setIdFrontUploaded(true);
    } else if (documentType === 'id-back') {
      setIdBackUploaded(true);
    } else if (documentType === 'address') {
      setAddressDocUploaded(true);
    } else if (documentType === 'bank-statement') {
      setBankStatementUploaded(true);
    }
  };
  // Updated completion logic - bank statement is now mandatory
  const isComplete = bankStatementUploaded && addressDocUploaded && (idType === '' || idType && idFrontUploaded && (idType === 'passport' || idBackUploaded));
  return <div className="space-y-6" data-id="element-739">
      <div data-id="element-740">
        <h2 className="text-lg font-medium text-gray-900" data-id="element-741">
          Identity Verification (KYC)
        </h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-742">
          To comply with regulations and ensure security, we need to verify your
          identity.
        </p>
      </div>
      {/* Bank statement section - now mandatory */}
      <div className="border border-gray-300 rounded-md p-4" data-id="element-743">
        <div className="flex items-start" data-id="element-744">
          <div className="flex-shrink-0" data-id="element-745">
            {bankStatementUploaded ? <CheckIcon className="h-5 w-5 text-green-500" data-id="element-746" /> : <FileTextIcon className="h-5 w-5 text-gray-400" data-id="element-747" />}
          </div>
          <div className="ml-3 flex-1" data-id="element-748">
            <div className="flex items-center" data-id="element-749">
              <h3 className="text-sm font-medium text-gray-900" data-id="element-750">
                Bank Statement
              </h3>
              <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800" data-id="element-751">
                Required
              </span>
            </div>
            <p className="text-xs text-gray-500" data-id="element-752">
              Upload a recent bank statement (less than 3 months old) showing
              your name and address. This is required to verify your identity
              after bank account verification.
            </p>
            {!bankStatementUploaded && <div className="mt-2" data-id="element-753">
                <button type="button" onClick={() => handleUpload('bank-statement')} className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-754">
                  Upload Bank Statement
                </button>
              </div>}
            {bankStatementUploaded && <p className="mt-1 text-xs text-green-600" data-id="element-755">
                Bank statement uploaded successfully
              </p>}
          </div>
        </div>
      </div>
      {/* Proof of address is required for all verifications */}
      <div className="border border-gray-300 rounded-md p-4" data-id="element-756">
        <div className="flex items-start" data-id="element-757">
          <div className="flex-shrink-0" data-id="element-758">
            {addressDocUploaded ? <CheckIcon className="h-5 w-5 text-green-500" data-id="element-759" /> : <UploadIcon className="h-5 w-5 text-gray-400" data-id="element-760" />}
          </div>
          <div className="ml-3 flex-1" data-id="element-761">
            <div className="flex items-center" data-id="element-762">
              <h3 className="text-sm font-medium text-gray-900" data-id="element-763">
                Proof of Address
              </h3>
              <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800" data-id="element-764">
                Required
              </span>
            </div>
            <p className="text-xs text-gray-500" data-id="element-765">
              Upload a utility bill, bank statement, or government letter (less
              than 3 months old)
            </p>
            {!addressDocUploaded && <div className="mt-2" data-id="element-766">
                <button type="button" onClick={() => handleUpload('address')} className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-767">
                  Upload
                </button>
              </div>}
            {addressDocUploaded && <p className="mt-1 text-xs text-green-600" data-id="element-768">
                Uploaded successfully
              </p>}
          </div>
        </div>
      </div>
      {/* Optional additional ID verification */}
      <div data-id="element-769">
        <div className="flex items-center justify-between" data-id="element-770">
          <label htmlFor="id-type" className="block text-sm font-medium text-gray-700" data-id="element-771">
            Additional ID Verification (Optional)
          </label>
          {idType && <span className="text-xs text-gray-500" data-id="element-772">
              You can provide additional identification for enhanced
              verification
            </span>}
        </div>
        <select id="id-type" name="id-type" value={idType} onChange={e => setIdType(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" data-id="element-773">
          <option value="" data-id="element-774">None (Bank Statement Only)</option>
          <option value="passport" data-id="element-775">Passport</option>
          <option value="drivers-license" data-id="element-776">Driver's License</option>
          <option value="national-id" data-id="element-777">National ID Card</option>
        </select>
      </div>
      {idType && <div className="space-y-4" data-id="element-778">
          <div className="border border-gray-300 rounded-md p-4" data-id="element-779">
            <div className="flex items-start" data-id="element-780">
              <div className="flex-shrink-0" data-id="element-781">
                {idFrontUploaded ? <CheckIcon className="h-5 w-5 text-green-500" data-id="element-782" /> : <UploadIcon className="h-5 w-5 text-gray-400" data-id="element-783" />}
              </div>
              <div className="ml-3 flex-1" data-id="element-784">
                <h3 className="text-sm font-medium text-gray-900" data-id="element-785">
                  {idType === 'passport' ? 'Passport Photo Page' : 'ID Front Side'}
                </h3>
                <p className="text-xs text-gray-500" data-id="element-786">
                  Upload a clear photo of the{' '}
                  {idType === 'passport' ? 'photo page' : 'front side'} of your{' '}
                  {idType.replace('-', ' ')}
                </p>
                {!idFrontUploaded && <div className="mt-2" data-id="element-787">
                    <button type="button" onClick={() => handleUpload('id-front')} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-788">
                      Upload
                    </button>
                  </div>}
                {idFrontUploaded && <p className="mt-1 text-xs text-green-600" data-id="element-789">
                    Uploaded successfully
                  </p>}
              </div>
            </div>
          </div>
          {idType !== 'passport' && <div className="border border-gray-300 rounded-md p-4" data-id="element-790">
              <div className="flex items-start" data-id="element-791">
                <div className="flex-shrink-0" data-id="element-792">
                  {idBackUploaded ? <CheckIcon className="h-5 w-5 text-green-500" data-id="element-793" /> : <UploadIcon className="h-5 w-5 text-gray-400" data-id="element-794" />}
                </div>
                <div className="ml-3 flex-1" data-id="element-795">
                  <h3 className="text-sm font-medium text-gray-900" data-id="element-796">
                    ID Back Side
                  </h3>
                  <p className="text-xs text-gray-500" data-id="element-797">
                    Upload a clear photo of the back side of your{' '}
                    {idType.replace('-', ' ')}
                  </p>
                  {!idBackUploaded && <div className="mt-2" data-id="element-798">
                      <button type="button" onClick={() => handleUpload('id-back')} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-799">
                        Upload
                      </button>
                    </div>}
                  {idBackUploaded && <p className="mt-1 text-xs text-green-600" data-id="element-800">
                      Uploaded successfully
                    </p>}
                </div>
              </div>
            </div>}
        </div>}
      {!bankStatementUploaded && <div className="rounded-md bg-yellow-50 p-4" data-id="element-801">
          <div className="flex" data-id="element-802">
            <div className="flex-shrink-0" data-id="element-803">
              <AlertCircleIcon className="h-5 w-5 text-yellow-400" data-id="element-804" />
            </div>
            <div className="ml-3" data-id="element-805">
              <h3 className="text-sm font-medium text-yellow-800" data-id="element-806">
                Bank statement required
              </h3>
              <div className="mt-2 text-sm text-yellow-700" data-id="element-807">
                <p data-id="element-808">
                  After completing bank account verification, you must upload
                  your bank statement to verify your identity and continue with
                  the onboarding process.
                </p>
              </div>
            </div>
          </div>
        </div>}
      <div className="pt-5" data-id="element-809">
        <div className="flex justify-end" data-id="element-810">
          <button type="button" disabled={!isComplete} onClick={onNext} className={`ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${!isComplete ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'}`} data-id="element-811">
            Complete Setup
          </button>
        </div>
      </div>
    </div>;
};
export default KYCVerification;